import type { LegacyRef, CSSProperties } from "react";
import { useMemo } from "react";

import type { ScrollArea } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import DropdownEmptyItem from "app/pages/sales-intelligence/common-components/dropdown/DropdownEmptyItem/DropdownEmptyItem";
import { opportunityListOptionNameIncludes } from "app/pages/sales-intelligence/sub-modules/opportunities/helpers/helpers";
import ScrollableDropdownContainer from "app/pages/workspace/sales/components/custom-dropdown/ScrollableDropdownContainer/ScrollableDropdownContainer";

import type { OpportunityListOption } from "../../../../types";
import { OpportunityListVariant } from "../../../../types";
import ListsDropdownItem from "../ListsDropdownItem/ListsDropdownItem";
import { StyledListDropdownGroupItem } from "../ListsDropdownItem/styles";

import * as styles from "./styles";

const SCROLL_AREA_STYLES: CSSProperties = { maxHeight: 225 };

type ListsDropdownContentProps = {
    open: boolean;
    search: string;
    groupedLists: Record<OpportunityListVariant, readonly OpportunityListOption[]>;
    selectedList: OpportunityListOption;
    setDropdownScrollAreaRef?: LegacyRef<ScrollArea>;
    onSearch: (q: string) => void;
    onSelect: (list: OpportunityListOption) => void;
};

const ListsDropdownContent = (props: ListsDropdownContentProps) => {
    const { open, search, onSearch, groupedLists, onSelect, selectedList, setDropdownScrollAreaRef } = props;
    const searchProps = useMemo(() => {
        return {
            value: search,
            onChange: onSearch,
            placeholder: translate("si.components.lists_dropdown.placeholder"),
        };
    }, [search, onSearch]);

    const applyFilters = (options: readonly OpportunityListOption[]) => {
        return options.filter((l) => l.id !== selectedList.id).filter(opportunityListOptionNameIncludes(search));
    };

    const filteredCrmListOptions = applyFilters(groupedLists[OpportunityListVariant.Crm]);
    const filteredStaticListOptions = applyFilters(groupedLists[OpportunityListVariant.Static]);

    const totalOptionsCount = filteredCrmListOptions.length + filteredStaticListOptions.length;

    const shouldRenderGroupItems = filteredCrmListOptions.length > 0 && filteredStaticListOptions.length > 0;

    return (
        <styles.StyledListsDropdownContent
            includesSearch
            searchProps={searchProps}
            open={open}
        >
            <ScrollableDropdownContainer
                scrollAreaStyle={SCROLL_AREA_STYLES}
                setScrollAreaRef={setDropdownScrollAreaRef}
            >
                {shouldRenderGroupItems ? (
                    <StyledListDropdownGroupItem>
                        {translate("si.static-lists.list-selector.crm-lists")}
                    </StyledListDropdownGroupItem>
                ) : null}
                {filteredCrmListOptions.map((list) => (
                    <ListsDropdownItem
                        list={list}
                        onClick={onSelect}
                        key={list.id}
                    />
                ))}
                {shouldRenderGroupItems ? (
                    <StyledListDropdownGroupItem>
                        {translate("si.static-lists.list-selector.static-lists")}
                    </StyledListDropdownGroupItem>
                ) : null}
                {filteredStaticListOptions.map((list) => (
                    <ListsDropdownItem
                        list={list}
                        onClick={onSelect}
                        key={list.id}
                    />
                ))}
                {totalOptionsCount === 0 ? (
                    <DropdownEmptyItem text={translate("si.components.lists_dropdown.empty_text")} />
                ) : null}
            </ScrollableDropdownContainer>
        </styles.StyledListsDropdownContent>
    );
};

export default ListsDropdownContent;
